import { STUDENT_TOKEN } from 'apis/arPlaybookApi'

import { parseJwt } from 'utils/helper'

import { setStudentToken } from '../student/helper'
import { IStudentTokenData } from './bookAccess/types'

export const STUDENT_TOKEN_REGISTRY = 'student-book-tokens'
/** Key within registry that points to the current active book */
const CURRENT_BOOK_KEY = '_current'

// ============================================================================
// Types
// ============================================================================

export interface ITokenRegistryEntry {
    token: string
    bookSlug: string
    businessSlug: string
    bookName?: string
    businessName?: string
    addedAt: number
}

export interface ITokenRegistry {
    /** Pointer to the currently active book (e.g., "business-slug/book-slug") */
    _current?: string
    /** Token entries keyed by "businessSlug/bookSlug" */
    [key: string]: ITokenRegistryEntry | string | undefined
}

export interface ITokenData {
    bookName?: string
    businessSlug?: string
    bookSlug?: string
    // Legacy tokens only have bookName
}

// ============================================================================
// Registry Functions
// ============================================================================

/**
 * Get the token registry from localStorage
 */
export const getTokenRegistry = (): ITokenRegistry => {
    try {
        const registryJson = localStorage.getItem(STUDENT_TOKEN_REGISTRY)
        if (!registryJson) {
            return {}
        }
        return JSON.parse(registryJson) as ITokenRegistry
    } catch {
        console.error('Failed to parse token registry')
        return {}
    }
}

/**
 * Save the token registry to localStorage
 */
const saveTokenRegistry = (registry: ITokenRegistry): void => {
    localStorage.setItem(STUDENT_TOKEN_REGISTRY, JSON.stringify(registry))
}

/**
 * Generate a registry key from business and book slugs
 */
export const getRegistryKey = (businessSlug: string, bookSlug: string): string => {
    return `${businessSlug}/${bookSlug}`
}

/**
 * Get the currently active book's token from the registry
 * @returns The current token if set, null otherwise
 */
export const getCurrentToken = (): string | null => {
    const registry = getTokenRegistry()
    const currentKey = registry[CURRENT_BOOK_KEY]
    if (!currentKey || typeof currentKey !== 'string') {
        return null
    }
    const entry = registry[currentKey]
    if (entry && typeof entry === 'object' && 'token' in entry) {
        return entry.token
    }
    return null
}

/**
 * Set the current active book in the registry
 */
export const setCurrentBook = (businessSlug: string, bookSlug: string): void => {
    const registry = getTokenRegistry()
    const key = getRegistryKey(businessSlug, bookSlug)
    const entry = registry[key]

    // Only set if the book exists in registry
    if (entry && typeof entry === 'object' && 'token' in entry) {
        registry[CURRENT_BOOK_KEY] = key
        saveTokenRegistry(registry)

        // FIXME: Remove after Jan 2027 - Legacy compatibility
        setStudentToken(entry.token)
    }
}

/**
 * Check if a token for a specific book is already registered
 */
export const isTokenRegistered = (businessSlug: string, bookSlug: string): boolean => {
    const registry = getTokenRegistry()
    const key = getRegistryKey(businessSlug, bookSlug)
    const entry = registry[key]
    return !!(entry && typeof entry === 'object' && 'token' in entry)
}

/**
 * Add or update a token in the registry and set it as current.
 * If the token is already registered with the same value, only sets it as current.
 * Also updates the legacy STUDENT_TOKEN for backwards compatibility.
 *
 * @returns true if the token was newly registered, false if it was already present
 *
 * FIXME: Remove STUDENT_TOKEN write after Jan 2027
 */
export const registerToken = (
    token: string,
    businessSlug: string,
    bookSlug: string,
    bookName?: string,
    businessName?: string,
): boolean => {
    const registry = getTokenRegistry()
    const key = getRegistryKey(businessSlug, bookSlug)
    const existingEntry = registry[key]

    // Check if already registered with same token
    const isAlreadyRegistered =
        existingEntry &&
        typeof existingEntry === 'object' &&
        'token' in existingEntry &&
        existingEntry.token === token

    if (!isAlreadyRegistered) {
        // Register new or update existing
        registry[key] = {
            token,
            bookSlug,
            businessSlug,
            bookName,
            businessName,
            addedAt: Date.now(),
        }
    }

    // Always set this as the current active book
    registry[CURRENT_BOOK_KEY] = key

    saveTokenRegistry(registry)

    // FIXME: Remove after Jan 2027 - Legacy compatibility for code reading STUDENT_TOKEN directly
    setStudentToken(token)

    return !isAlreadyRegistered
}

/**
 * Get a token for a specific book from the registry
 * @returns The token if found, null otherwise
 */
export const getTokenForBook = (businessSlug: string, bookSlug: string): string | null => {
    const registry = getTokenRegistry()
    const key = getRegistryKey(businessSlug, bookSlug)
    const entry = registry[key]
    if (entry && typeof entry === 'object' && 'token' in entry) {
        return entry.token
    }
    return null
}

/**
 * Remove a token from the registry
 */
export const removeToken = (businessSlug: string, bookSlug: string): void => {
    const registry = getTokenRegistry()
    const key = getRegistryKey(businessSlug, bookSlug)

    // If removing the current book, clear the current pointer
    if (registry[CURRENT_BOOK_KEY] === key) {
        delete registry[CURRENT_BOOK_KEY]
    }

    delete registry[key]
    saveTokenRegistry(registry)
}

/**
 * Get all registered tokens (excludes the _current pointer)
 * @returns Array of registry entries
 */
export const getAllTokens = (): ITokenRegistryEntry[] => {
    const registry = getTokenRegistry()
    return Object.entries(registry)
        .filter(([key, value]) => key !== CURRENT_BOOK_KEY && typeof value === 'object')
        .map(([, value]) => value as ITokenRegistryEntry)
}

/**
 * Clear all tokens from the registry
 */
export const clearAllTokens = (): void => {
    localStorage.removeItem(STUDENT_TOKEN_REGISTRY)
    localStorage.removeItem(STUDENT_TOKEN)
}

/**
 * Migrate a legacy token to the new registry format
 * Call this after successful book load with legacy token
 *
 * FIXME: Remove after Jan 2027
 */
export const migrateLegacyToken = (
    token: string,
    businessSlug: string,
    bookSlug: string,
    bookName?: string,
    businessName?: string,
): void => {
    registerToken(token, businessSlug, bookSlug, bookName, businessName)
}

/**
 * Migrate legacy STUDENT_TOKEN to the registry on app load
 * This ensures existing users get their token into the registry
 * without requiring them to re-authenticate.
 *
 * Call this once on app initialization.
 *
 * FIXME: Remove after Jan 2027
 */
export const migrateLegacyTokenOnLoad = (): void => {
    const registry = getTokenRegistry()

    // If registry already has entries (excluding _current), skip migration
    const hasEntries = Object.keys(registry).some(
        key => key !== CURRENT_BOOK_KEY && typeof registry[key] === 'object',
    )
    if (hasEntries) {
        return
    }

    // Check for legacy token
    const legacyToken = localStorage.getItem(STUDENT_TOKEN)
    if (!legacyToken) {
        return
    }

    // Parse and migrate
    const tokenInfo = parseTokenInfo(legacyToken)
    if (tokenInfo && tokenInfo.businessSlug && tokenInfo.bookSlug) {
        registerToken(legacyToken, tokenInfo.businessSlug, tokenInfo.bookSlug, tokenInfo.bookName)
        console.log('Migrated legacy student token to registry')
    }
}

/**
 * Parse token to extract book and business info
 */
export const parseTokenInfo = (
    token: string,
): { bookName?: string; bookSlug?: string; businessSlug?: string } | null => {
    try {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const { data } = parseJwt(token)
        return {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            bookName: data?.bookName as string | undefined,
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            bookSlug: data?.bookSlug as string | undefined,
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            businessSlug: data?.businessSlug as string | undefined,
        }
    } catch {
        return null
    }
}

/**
 * Find a student token by license key.
 * Searches all registered tokens for one that contains the given license key.
 *
 * @param licenseKey - The license key to search for
 * @returns The JWT token if found, null otherwise
 */
export const findTokenByLicenseKey = (licenseKey: string): string | null => {
    const registry = getTokenRegistry()

    // Search all registered tokens
    for (const key of Object.keys(registry)) {
        // Skip the _current pointer
        if (key === CURRENT_BOOK_KEY) {
            continue
        }

        const entry = registry[key]
        if (entry && typeof entry === 'object' && 'token' in entry) {
            const tokenData = parseStudentToken(entry.token)
            if (tokenData && tokenData.licenseKey === licenseKey) {
                return entry.token
            }
        }
    }

    // Also check legacy token
    const legacyToken = localStorage.getItem(STUDENT_TOKEN)
    if (legacyToken) {
        const tokenData = parseStudentToken(legacyToken)
        if (tokenData && tokenData.licenseKey === licenseKey) {
            return legacyToken
        }
    }

    return null
}

// ============================================================================
// V2 Token Helpers
// ============================================================================

/**
 * Parse a token and extract full student data including bookAccess
 *
 * @param token - JWT token string
 * @returns Parsed token data or null if parsing fails
 */
export const parseStudentToken = (token: string): IStudentTokenData | null => {
    try {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const { data } = parseJwt(token)
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        return data as IStudentTokenData
    } catch {
        return null
    }
}
